/**
 * Data layer for `igaming-chargeback-refund-events`. Thin typed fetch wrappers over /api/v1/igaming-chargeback-refund-events.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface IgamingChargebackRefundEventsRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListIgamingChargebackRefundEventsResponse {
	count: number;
	items: IgamingChargebackRefundEventsRow[];
}

export interface CreateIgamingChargebackRefundEventsBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/igaming-chargeback-refund-events";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListIgamingChargebackRefundEventsResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListIgamingChargebackRefundEventsResponse>(res);
}

export async function create(body: CreateIgamingChargebackRefundEventsBody): Promise<IgamingChargebackRefundEventsRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<IgamingChargebackRefundEventsRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const igamingChargebackRefundEventsQueryKeys = {
	all: ["igaming-chargeback-refund-events"] as const,
	list: () => [...igamingChargebackRefundEventsQueryKeys.all] as const,
	detail: (id: string) => [...igamingChargebackRefundEventsQueryKeys.all, id] as const,
};
